import { useMemo } from 'react';
import { useSelf } from 'App/components/hooks/appHooks';
import { PageIframeInit } from './PageIframe';
import AppService from 'App/legacy/AppService';

/** The record a Page renders inside, besides its company. Each id backs one SDK hook:
 * `useOrder()`, `useProject()`, `useContact()`, `useAgreement()`, `useAppointment()`. */
export type PageRecordIds = {
	orderId?: number | null;
	projectId?: number | null;
	contactId?: number | null;
	agreementId?: number | null;
	appointmentId?: number | null;
};

/**
 * Builds the `init` payload posted into a sandboxed Page iframe from the
 * current session. `clientId` scopes the page to a company so its
 * `useAccount()` hook resolves; `record` scopes it to the order, project,
 * contact, agreement or appointment it is placed in. Leave out what the host
 * isn't scoped to.
 *
 * Shared by every Page host (standalone `PageView`, the company-card embed,
 * the record embeds, the authoring preview) so the payload shape stays in
 * one place.
 */
export function usePageIframeInit(clientId: number | null, record: PageRecordIds = {}): PageIframeInit {
	const self = useSelf();
	const language: string | null = (self as { language?: string } | null)?.language ?? null;
	// Tenant id — the `:customerId` segment of every in-app hash route. Resolved
	// the same way `openCrmEntity` does so Page deep links match the host's URLs.
	const rawCustomerId = AppService.getCustomerId();
	const customerId: number | null = rawCustomerId != null ? Number(rawCustomerId) : null;
	const orderId = record.orderId ?? null;
	const projectId = record.projectId ?? null;
	const contactId = record.contactId ?? null;
	const agreementId = record.agreementId ?? null;
	const appointmentId = record.appointmentId ?? null;

	return useMemo<PageIframeInit>(
		() => ({
			userId: self?.id ?? null,
			customerId,
			clientId,
			orderId,
			projectId,
			contactId,
			agreementId,
			appointmentId,
			language,
			currentRoute: typeof window !== 'undefined' ? window.location.hash || null : null
		}),
		[self?.id, customerId, language, clientId, orderId, projectId, contactId, agreementId, appointmentId]
	);
}
